import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Search, Clock, ArrowUpRight, FileText, Globe, Filter
} from 'lucide-react';
import CountryFlag from '../components/CountryFlag';
import { countriesApi } from '../lib/api';

const DestinationsPage = () => {
  const [countries, setCountries] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const res = await countriesApi.getAll();
        setCountries(res.data);
      } catch (error) {
        console.error('Error fetching countries:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCountries();


  const saved = sessionStorage.getItem('destination_search');
  if (saved) {
    setSearch(saved);
    sessionStorage.removeItem('destination_search'); // clear after use
  }
  }, []);

  const filtered = countries.filter((c) => {
    const matchSearch = c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.code?.toLowerCase().includes(search.toLowerCase());
    if (filter === 'popular') return matchSearch && c.is_popular;
    return matchSearch;
  });

  const getDeliveryDate = (days) => {
    const d = new Date();
    d.setDate(d.getDate() + days);
    return d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  const getRandomProcessed = (name) => {
    const hash = name.split('').reduce((a, c) => a + c.charCodeAt(0), 0);
    return ((hash % 80) + 20) + 'K+';
  };

  return (
    <div className="min-h-screen bg-[#F5F0E6]">
      <Navbar />
      <SEO
        title="Countries - Visa Destinations"
        description="Browse 150+ countries for visa applications. Find visa types, requirements, processing times, and fees for your dream destination."
        keywords="visa countries, visa destinations, tourist visa countries, business visa countries, apply visa online"
        canonical="/countries"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": "Visa Countries",
          "description": "Browse countries for visa applications with EasyMyVisa",
          "url": `${window.location.origin}/countries`
        }}
      />
      
      <main className="pt-24 pb-16 px-4" data-testid="destinations-page">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-[#0A1E40]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              All Countries
            </h1>
            <p className="text-[#4A5568] mt-2">Browse 150+ countries and find your perfect visa</p>
          </div>

          {/* Search & Filter Bar */}
          <div className="flex flex-col md:flex-row gap-3 mb-8">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9CA3AF]" />
              <Input
                type="text"
                placeholder="Search countries..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-12"
                data-testid="search-input"
              />
            </div>
            <div className="flex gap-2">
              {[
                { key: 'all', label: 'All' },
                { key: 'popular', label: 'Popular' }
              ].map(f => (
                <Button
                  key={f.key}
                  variant={filter === f.key ? 'default' : 'outline'}
                  className={filter === f.key ? 'bg-[#0A1E40] text-white' : 'border-[#D1CBBF]'}
                  onClick={() => setFilter(f.key)}
                  data-testid={`filter-${f.key}`}
                >
                  {f.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Country Cards Grid */}
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="bg-white rounded-2xl border border-[#D1CBBF] overflow-hidden animate-pulse">
                  <div className="h-40 bg-gray-200" />
                  <div className="p-4 space-y-3">
                    <div className="h-4 bg-gray-200 rounded w-2/3" />
                    <div className="h-3 bg-gray-200 rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <>
              <p className="text-sm text-[#4A5568] mb-4">{filtered.length} countries found</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 stagger-children">
                {filtered.map((country) => (
                  <Link
                    key={country.country_id}
                    to={`/countries/${country.code?.toLowerCase()}`}
                    className="group bg-white rounded-2xl overflow-hidden border border-[#D1CBBF] card-hover"
                    data-testid={`destination-card-${country.code}`}
                  >
                    {/* Card Image */}
                    <div className="relative h-40 overflow-hidden">
                      <img
                        src={country.image_url}
                        alt={country.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />

                      {country.is_popular && (
                        <div className="absolute top-3 right-3 bg-[#FF5A36] text-white px-3 py-1 rounded-full text-xs font-bold">
                          Popular
                        </div>
                      )}

                      <div className="absolute bottom-3 left-3 flex items-center gap-2">
                        <CountryFlag code={country.code} size="lg" />
                        <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                          {country.name}
                        </h3>
                      </div>
                    </div>

                    {/* Card Body */}
                    <div className="p-4">
                      <div className="flex items-center gap-4 text-xs text-[#4A5568] mb-3">
                        <span className="flex items-center gap-1">
                          <FileText className="w-3.5 h-3.5" /> e-Visa
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5" /> 3-5 days
                        </span>
                      </div>

                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {(country.requirements || []).slice(0, 3).map((req, i) => (
                          <span key={i} className="inline-flex items-center px-2 py-0.5 rounded-md bg-[#F5F0E6] text-xs text-[#4A5568]">
                            {req.length > 15 ? req.substring(0, 15) + '...' : req}
                          </span>
                        ))}
                      </div>

                      <div className="flex items-center justify-end pt-3 border-t border-[#F5F0E6]">
                        <div className="w-8 h-8 rounded-full bg-[#FF5A36] flex items-center justify-center group-hover:scale-110 transition-transform">
                          <ArrowUpRight className="w-4 h-4 text-white" />
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              {filtered.length === 0 && (
                <div className="text-center py-16">
                  <Globe className="w-16 h-16 text-[#D1CBBF] mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-[#0A1E40] mb-2">No destinations found</h3>
                  <p className="text-[#4A5568]">Try a different search term</p>
                </div>
              )}
            </>
          )}
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default DestinationsPage;
